import React, { useEffect, useState, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { getPageContainerStyles, getSearchFormStyles, getTableStyles } from '../../utils/styleUtils';
import { hasAdminAccess } from '../../utils/permissionConfig';
import { api } from '../../services/api';

const TEXT = {
  title: {
    ko: '회원 관리',
    en: 'Users Management',
    fr: 'Gestion des utilisateurs'
  },
  loading: {
    ko: '로딩 중...',
    en: 'Loading...',
    fr: 'Chargement...'
  },
  noUsers: {
    ko: '회원이 없습니다.',
    en: 'No users found.',
    fr: 'Aucun utilisateur trouvé.'
  },
  deleteConfirm: {
    ko: '이 회원을 삭제하시겠습니까?',
    en: 'Are you sure you want to delete this user?',
    fr: 'Êtes-vous sûr de vouloir supprimer cet utilisateur?'
  },
  delete: {
    ko: '삭제',
    en: 'Delete',
    fr: 'Supprimer'
  },
  // 테이블 헤더 다국어
  headers: {
    userId: {
      ko: '사용자 ID',
      en: 'User ID',
      fr: 'ID Utilisateur'
    },
    loginId: {
      ko: '로그인 ID',
      en: 'Login ID',
      fr: 'ID Connexion'
    },
    userName: {
      ko: '사용자명',
      en: 'User Name',
      fr: 'Nom Utilisateur'
    },
    email: {
      ko: '이메일',
      en: 'Email',
      fr: 'Email'
    },
    joinedAt: {
      ko: '가입일',
      en: 'Joined Date',
      fr: 'Date d\'inscription'
    },
    status: {
      ko: '상태',
      en: 'Status',
      fr: 'Statut'
    },
    location: {
      ko: '위치',
      en: 'Location',
      fr: 'Localisation'
    },
    level: {
      ko: '레벨',
      en: 'Level',
      fr: 'Niveau'
    },
    color: {
      ko: '색상',
      en: 'Color',
      fr: 'Couleur'
    }
  }
};

const Users = () => {
  const { userInfo, isUserLoading } = useContext(UserContext);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lang = userInfo?.preferred_language || 'ko';

  // 공통 스타일 가져오기
  const pageContainerStyles = getPageContainerStyles();
  const searchStyles = getSearchFormStyles();
  const tableStyles = getTableStyles();

  useEffect(() => {
    if (hasAdminAccess(userInfo)) {
      setLoading(true);
      api.get('/admin/users')
        .then((data) => {
          console.log('사용자 목록 로드 성공:', data);
          setUsers(Array.isArray(data?.users) ? data.users : []);
          setError(null);
        })
        .catch((err) => {
          console.error('사용자 목록 로드 실패:', err);
          setError(err?.data?.message || '사용자 목록을 불러오는데 실패했습니다.');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      console.log('관리자 권한이 없습니다.');
    }
  }, [userInfo]);

  const handleEdit = (userId, key, value) => {
    console.log('수정 시도:', { userId, key, value });
    setLoading(true);
    setError(null);

    api.put(`/admin/users/${userId}`, { [key]: value })
      .then((data) => {
        console.log('수정 성공:', data);
        setUsers((prev) =>
          prev.map((u) =>
            u.user_id === userId ? { ...u, [key]: value } : u
          )
        );
      })
      .catch((err) => {
        console.error('수정 실패:', err);
        console.error('에러 응답:', err.data);
        setError(`회원 정보 수정에 실패했습니다: ${err?.data?.message || err.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDelete = (userId) => {
    console.log('삭제 시도:', userId);

    if (!window.confirm(TEXT.deleteConfirm[lang])) {
      console.log('삭제 취소됨');
      return;
    }

    setLoading(true);
    setError(null);

    api.delete(`/admin/users/${userId}`)
      .then((data) => {
        console.log('삭제 성공:', data);
        setUsers((prev) => prev.filter((u) => u.user_id !== userId));
        alert('회원이 성공적으로 삭제되었습니다.');
      })
      .catch((err) => {
        console.error('삭제 실패:', err);
        console.error('에러 응답:', err.data);
        setError(`회원 삭제에 실패했습니다: ${err?.data?.message || err.message}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const editableKeys = ['user_login_id', 'user_name', 'email', 'status', 'location', 'level', 'color'];

  // 로딩 중이거나 사용자 정보가 없는 경우
  if (isUserLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#666'
      }}>
        사용자 정보를 불러오는 중...
      </div>
    );
  }

  // 사용자 정보가 없는 경우
  if (!userInfo) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#666'
      }}>
        로그인이 필요합니다.
      </div>
    );
  }

  // admin 또는 특수 사용자가 아닌 경우
  if (!hasAdminAccess(userInfo)) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        fontSize: '16px',
        color: '#666'
      }}>
        관리자 권한이 필요합니다.
      </div>
    );
  }

  return (
    <div className={pageContainerStyles.pageContainer} style={{
      height: '100%',
      overflow: 'auto',
      paddingBottom: '100px',
      boxSizing: 'border-box'
    }}>
      {/* 페이지 타이틀 */}
      <h1 className={pageContainerStyles.pageTitle}>
        {TEXT.title[lang]}
      </h1>

      {/* 로딩 및 에러 메시지 */}
      {loading && <p style={{ margin: '0 0 15px 0', fontSize: '14px' }}>{TEXT.loading[lang]}</p>}
      {error && <p style={{ color: 'red', margin: '0 0 15px 0', fontSize: '14px' }}>{error}</p>}

      {/* 테이블 컨테이너 */}
      <div className={tableStyles.tableContainer}>
        <table className={tableStyles.table}>
          <thead className={tableStyles.tableHeader}>
            <tr>
              <th>{TEXT.headers.userId[lang]}</th>
              <th>{TEXT.headers.loginId[lang]}</th>
              <th>{TEXT.headers.userName[lang]}</th>
              <th>{TEXT.headers.email[lang]}</th>
              <th>{TEXT.headers.joinedAt[lang]}</th>
              <th>{TEXT.headers.status[lang]}</th>
              <th>{TEXT.headers.location[lang]}</th>
              <th>{TEXT.headers.level[lang]}</th>
              <th>{TEXT.headers.color[lang]}</th>
              <th>{TEXT.delete[lang]}</th>
            </tr>
          </thead>
          <tbody className={tableStyles.tableBody}>
            {Array.isArray(users) && users.map((u) => (
              <tr key={u.user_id} className={tableStyles.tableRow}>
                <td>{u.user_id}</td>
                {['user_login_id', 'user_name', 'email', 'joined_at', 'status', 'location', 'level', 'color'].map((key) => (
                  <td
                    key={key}
                    style={{ cursor: editableKeys.includes(key) ? 'pointer' : 'default' }}
                    onClick={() =>
                      editableKeys.includes(key) &&
                      setEditing({ userId: u.user_id, key, value: u[key] })
                    }
                  >
                    {editing.userId === u.user_id && editing.key === key ? (
                      key === 'color' ? (
                        <input
                          type="color"
                          value={editing.value}
                          onChange={(e) =>
                            setEditing((prev) => ({ ...prev, value: e.target.value }))
                          }
                          onBlur={() => {
                            if (editing.value !== u[key]) {
                              handleEdit(u.user_id, key, editing.value);
                            }
                            setEditing({});
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (editing.value !== u[key]) {
                                handleEdit(u.user_id, key, editing.value);
                              }
                              setEditing({});
                            }
                            if (e.key === 'Escape') {
                              setEditing({});
                            }
                          }}
                          autoFocus
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <input
                          value={editing.value}
                          onChange={(e) =>
                            setEditing((prev) => ({ ...prev, value: e.target.value }))
                          }
                          onBlur={() => {
                            if (editing.value !== u[key]) {
                              handleEdit(u.user_id, key, editing.value);
                            }
                            setEditing({});
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (editing.value !== u[key]) {
                                handleEdit(u.user_id, key, editing.value);
                              }
                              setEditing({});
                            }
                            if (e.key === 'Escape') {
                              setEditing({});
                            }
                          }}
                          autoFocus
                          style={{ width: '100%' }}
                        />
                      )
                    ) : key === 'joined_at' ? (
                      new Date(u[key]).toLocaleDateString('ko-KR')
                    ) : key === 'color' ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '20px', height: '20px', backgroundColor: u[key], border: '1px solid #ccc' }} />
                        <span>{u[key]}</span>
                      </div>
                    ) : (
                      u[key]
                    )}
                  </td>
                ))}
                <td>
                  <button
                    onClick={() => handleDelete(u.user_id)}
                    disabled={loading}
                    className={searchStyles.searchForm}
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                  >
                    {TEXT.delete[lang]}
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && !loading && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  {TEXT.noUsers[lang]}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;
